import type { Ref } from 'vue'
import type { ProductInfo } from './useProductInfo'
import type { NuxtAxiosInstance } from '@/types/axios'
import * as branchService from '@/service/branch'
import { BranchBookingServiceKey, BranchReserveMode } from '@/view-models/branch'
import type { BranchArrivalTimesQS, HeadcountsResult } from '@/view-models/branch'
import type { ArrivalTimesPickerData, ArrivalTimesPickerItem } from '@/view-models/booking'

function useBranchBookingOptions() {
  const i18n = useI18n()
  const productOptions = ref<ProductInfo[]>([])
  const headcountItems = ref<number[]>([])
  const arrivalTimesDayMap = ref<Partial<ArrivalTimesPickerData>>({
    symbol_code: '',
  })
  const headcountUIItems = computed(() =>
    headcountItems.value.map(num => ({
      text: `${i18n.t('number_people', { number: num })}`,
      value: num,
    }))
  )

  return {
    productOptions,
    headcountItems,
    headcountUIItems,
    arrivalTimesDayMap,
  }
}

export function useBranchBooking(bid = ref(0)) {
  const { $axios, $dayjs, $store } = useNuxtApp()
  const { productOptions, headcountItems, arrivalTimesDayMap, headcountUIItems } = useBranchBookingOptions()

  // for user picked data and API query params
  const bookingQueries = reactive({
    pid: 0,
    headcount: 0,
    bookingTime: '',
  })

  const apiServiceMap = {
    [BranchBookingServiceKey.Products]: branchService.getProducts,
    [BranchBookingServiceKey.Headcounts]: branchService.getHeadcounts,
    [BranchBookingServiceKey.ArrivalTimes]: getArrivalTimes,
  }

  async function invokeBookingSourceService(serviceKey: BranchBookingServiceKey) {
    try {
      const res = await apiServiceMap[serviceKey]?.($axios as any, bid.value, bookingQueries)

      return res?.data || null
    } catch (error) {
      console.error('invokeBookingSourceService:error', error)
      return null
    }
  }

  async function getArrivalTimes(
    axios: NuxtAxiosInstance,
    bid: number,
    params: BranchArrivalTimesQS
  ): Promise<{ data: Partial<ArrivalTimesPickerData> }> {
    try {
      const { data } = await branchService.getArrivalTimes(axios, bid, params)
      const { start_day, end_day, time_mode, timezone, selldates = {} } = data
      const arrivalTimes: Record<string, ArrivalTimesPickerItem[]> = {}
      const bestOffPeak: Record<string, boolean> = {}
      for (const [date, sellPayload] of Object.entries(selldates!)) {
        arrivalTimes[date] = sellPayload
          .flatMap(dateSellTimes => dateSellTimes.selltimes)
          .flatMap(({ selltime: sellTime, price, remain_count }) => ({
            date: $dayjs(sellTime).format('MM-DD'),
            discount: price || 0,
            price: price || 0,
            meridiem: $dayjs(sellTime).format('A'),
            selltime: sellTime,
            promostart: sellTime,
            remain_count: remain_count || 99,
            time: $dayjs(sellTime).format('HH:mm'),
          }))
        /**
         * TODO: 未來如果可能需要判斷「離峰最優惠」=> legacy logic: sellPayload.price === minPrice && sellPayload.price < srrp
         * * API 需要有 price, srrp 資訊
         */
        bestOffPeak[date] = false
      }

      const currency = $store.getters['currency/allCurrency'].find((currency: any) => currency.id === data.currency_id)

      return {
        data: {
          sellDates: selldates,
          arrivalTimes,
          bestOffPeak,
          start_day,
          end_day,
          time_mode,
          timezone,
          price: 0,
          symbol_code: currency?.code || '',
          symbol: data.currency_symbol || currency?.symbol || '',
        },
      }
    } catch (error) {
      console.error('getArrivalTimes:error', error)
      return {
        data: {},
      }
    }
  }

  function triggerService(serviceKey: BranchBookingServiceKey.ArrivalTimes): Promise<Partial<ArrivalTimesPickerData>>
  function triggerService(serviceKey: BranchBookingServiceKey.Headcounts): Promise<HeadcountsResult>
  function triggerService(serviceKey: BranchBookingServiceKey.Products): Promise<ProductInfo[]>
  async function triggerService(serviceKey: BranchBookingServiceKey): Promise<any> {
    try {
      const data = await invokeBookingSourceService(serviceKey)
      switch (serviceKey) {
        case BranchBookingServiceKey.Headcounts:
          headcountItems.value = (<HeadcountsResult>data)?.headcount
          break
        case BranchBookingServiceKey.ArrivalTimes:
          arrivalTimesDayMap.value = (data as Partial<ArrivalTimesPickerData>) || {}
          break
        case BranchBookingServiceKey.Products:
          productOptions.value = (data as ProductInfo[]) || []
          break
      }
      return data as any
    } catch (error) {
      console.error('doStepServiceFlow:error', error)
      throw error
    }
  }

  return {
    headcountItems,
    headcountUIItems,
    arrivalTimesDayMap,
    productOptions,
    bookingQueries,
    triggerService,
  }
}

export function useBranchBookingFlags(branch: Ref<any> = ref({})) {
  const nuxtApp = useNuxtApp()
  const reserveMode = computed(() => branch.value.reserve_mode)
  const isTimeFirst = computed(() => !nuxtApp.$store.state.groupOrderStatus && reserveMode.value === BranchReserveMode.TimeFirst)
  const isHeadcountFirst = computed(() => !nuxtApp.$store.state.groupOrderStatus && reserveMode.value === BranchReserveMode.HeadcountFirst)
  const isProductFirst = computed(
    () =>
      nuxtApp.$store.state.groupOrderStatus ||
      reserveMode.value === BranchReserveMode.ProductFirst ||
      !Object.values(BranchReserveMode).includes(reserveMode.value)
  )

  return {
    reserveMode,
    isProductFirst,
    isHeadcountFirst,
    isTimeFirst,
  }
}
